import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SignOut, Calendar, CurrencyCircleDollar, FloppyDisk, CircleNotch, Plus, Trash, GasPump, Key, TrendUp, TrendDown, Car, CalendarBlank, Wallet, CaretDown, CaretUp, Phone, FileText, CaretDoubleLeft, CaretDoubleRight, House, ChatCircle, MapPin, BellRinging, CalendarCheck, Gauge } from '@phosphor-icons/react';
import axios from 'axios';
import ChangePasswordModal from '../components/ChangePasswordModal';
import NotificationBell from '../components/NotificationBell';
import PushBell from '../components/shared/PushBell';
import { ThemeToggle } from '../components/shared/ThemeToggle';
import NotificationBanner from '../components/NotificationBanner';
import EmployeeYearlyStats from '../components/admin/EmployeeYearlyStats';
import EmployeePojazdyTab from '../components/employee/EmployeePojazdyTab';
import EmployeePaliwoTab from '../components/employee/EmployeePaliwoTab';
import EmployeePozyczkaTab from '../components/employee/EmployeePozyczkaTab';
import UrlowyTab from '../components/shared/UrlowyTab';
import KontaktyTab from '../components/shared/KontaktyTab';
import RejonyTab from '../components/shared/RejonyTab';
import PrzypomnieniaTab from '../components/shared/PrzypomnieniaTab';
import DyzuryTab from '../components/shared/DyzuryTab';
import DniowkiTab from '../components/shared/DniowkiTab';
import { payoutDateFromRecord } from '../utils/payoutDate';
import PrzebiegiTab from '../components/admin/PrzebiegiTab';
import EmployeeDocumentsTab from '../components/employee/EmployeeDocumentsTab';
import EmployeeHome from '../components/employee/EmployeeHome';
import ChatTab from '../components/shared/ChatTab';
import RefreshButton from '../components/shared/RefreshButton';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useChatBadge } from '../contexts/ChatBadgeContext';

// Always use current origin for API calls (works for any domain)
const API = `${window.location.origin}/api`;

const MONTHS = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const FIELD_LABELS = {
    przychod: 'Przychód',
    nota: 'Nota',
    // rata / paliwo są teraz pozycjami wewnątrz "Potrącenia" (nie osobne pola)
    premia: 'Premia',
    dochod: 'Dochód',
    przelew: 'Przelew'
};

// Функция для расчёта Podsumowanie
const calculatePodsumowanie = (monthRecords) => {
    if (monthRecords.length < 2) return null;
    const has01_15 = monthRecords.some(r => r.period_start === '01');
    // Support both "15" (old) and "16" (new)
    const has16_30 = monthRecords.some(r => r.period_start === '16' || r.period_start === '15');
    if (!has01_15 || !has16_30) return null;
    
    return {
        przychod: monthRecords.reduce((sum, r) => sum + (r.przychod || 0), 0),
        nota: monthRecords.reduce((sum, r) => sum + (r.nota || 0), 0),
        rata: monthRecords.reduce((sum, r) => sum + (parseFloat(r.rata) || 0), 0),
        paliwo: monthRecords.reduce((sum, r) => {
            const basePaliwo = r.paliwo || 0;
            const itemsSum = (r.paliwo_items || []).reduce((s, v) => s + (v || 0), 0);
            return sum + basePaliwo + itemsSum;
        }, 0),
        premia: monthRecords.reduce((sum, r) => sum + (r.premia || 0), 0),
        dochod: monthRecords.reduce((sum, r) => sum + (r.dochod || 0), 0),
        przelew: monthRecords.reduce((sum, r) => {
            const basePaliwo = r.paliwo || 0;
            const itemsSum = (r.paliwo_items || []).reduce((s, v) => s + (v || 0), 0);
            const totalPaliwo = basePaliwo + itemsSum;
            const customDeductionsTotal = (r.custom_deductions || []).reduce(
                (s, deduction) => s + (parseFloat(deduction.amount) || 0),
                0
            );
            const actualPrzelew = (r.dochod || 0) - totalPaliwo - (r.nota || 0) - (parseFloat(r.rata) || 0) - customDeductionsTotal;
            return sum + actualPrzelew;
        }, 0),
    };
};

// Расчёт przelew с учётом paliwo_items
const calculatePrzelew = (record) => {
    const basePaliwo = record.paliwo || 0;
    const itemsSum = (record.paliwo_items || []).reduce((s, v) => s + (v || 0), 0);
    const totalPaliwo = basePaliwo + itemsSum;
    
    // Calculate total custom deductions
    const customDeductionsTotal = (record.custom_deductions || []).reduce(
        (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
        0
    );
    
    return (record.dochod || 0) - totalPaliwo - (record.nota || 0) - (parseFloat(record.rata) || 0) - customDeductionsTotal;
};

// Сумма всех paliwo
const getTotalPaliwo = (record) => {
    const basePaliwo = record.paliwo || 0;
    const itemsSum = (record.paliwo_items || []).reduce((s, v) => s + (v || 0), 0);
    return basePaliwo + itemsSum;
};

export default function EmployeeDashboard() {
    const { user, logout } = useAuth();
    const { unread: chatUnread } = useChatBadge();
    // Init from URL ?tab=X so right-click "open in new tab" preserves selection.
    const _initialTab = (() => {
        try { return new URLSearchParams(window.location.search).get("tab") || "home"; }
        catch { return "home"; }
    })();
    const [tab, _setTab] = useState(_initialTab);
    const setTab = useCallback((next) => {
        _setTab(next);
        try {
            const url = new URL(window.location.href);
            if (next === "home") url.searchParams.delete("tab");
            else url.searchParams.set("tab", next);
            window.history.replaceState({}, "", url.toString());
        } catch { /* ignore */ }
    }, []);
    const [records, setRecords] = useState([]);
    const [allYearRecords, setAllYearRecords] = useState([]); // All records for the year (for percentage calculation)
    const [privateCredits, setPrivateCredits] = useState([]);
    const [loading, setLoading] = useState(true);
    // "New month" effectively starts on day 16 — see AdminDashboard for rationale.
    const _now = new Date();
    const _defaultMonthShift = _now.getDate() < 16 ? -1 : 0;
    const _defaultDate = new Date(_now.getFullYear(), _now.getMonth() + _defaultMonthShift, 1);
    const [selectedYear, setSelectedYear] = useState(_defaultDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(_defaultDate.getMonth() + 1); // Auto-filter to current month
    const [editingRecord, setEditingRecord] = useState(null);
    const [editValues, setEditValues] = useState({ rata: 0, paliwo: 0, paliwo_items: [] });
    const [saving, setSaving] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(null);
    const [expandedRecords, setExpandedRecords] = useState(new Set());
    // Per-record toggle for Potrącenia (collapsed sum ↔ detailed rows).
    const [expandedPotracenia, setExpandedPotracenia] = useState({});
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [bannerNotification, setBannerNotification] = useState(null);
    const [sidebarExpanded, setSidebarExpanded] = useState(() => {
        try { return localStorage.getItem('sidebar_expanded') === 'true'; } catch { return false; }
    });
    const [hasRejon, setHasRejon] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/rejony`);
                if (!cancelled) setHasRejon(Array.isArray(data) && data.length > 0);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, []);
    useEffect(() => {
        try { localStorage.setItem('sidebar_expanded', sidebarExpanded ? 'true' : 'false'); } catch { /* ignore */ }
    }, [sidebarExpanded]);

    // Fetch latest unread notification for banner
    useEffect(() => {
        const fetchLatestNotification = async () => {
            try {
                const { data } = await axios.get(`${API}/notifications`);
                const unread = data.find(n => !n.read);
                if (unread) setBannerNotification(unread);
            } catch (err) {
                console.error('Error fetching notifications:', err);
            }
        };
        fetchLatestNotification();
    }, []);

    useEffect(() => {
        if (tab === 'salary') {
            fetchRecords();
        } else if (tab === 'private-credits') {
            fetchPrivateCredits();
        }
    }, [tab, selectedYear, selectedMonth]);

    // Auto-refresh: keep the current tab's data fresh in a long-lived PWA tab.
    // Runs on 45s interval, visibility change, focus, and manual refresh button.
    useAutoRefresh(() => {
        if (tab === 'salary') fetchRecords();
        else if (tab === 'private-credits') fetchPrivateCredits();
    });

    // Check if password change is required on mount
    useEffect(() => {
        if (user?.password_change_required) {
            setShowChangePassword(true);
        }
    }, [user]);

    const fetchRecords = async () => {
        setLoading(true);
        try {
            // Load all records for the year (for percentage calculation)
            const yearParams = { year: selectedYear };
            const { data: yearData } = await axios.get(`${API}/salary`, { params: yearParams });
            setAllYearRecords(yearData);
            
            // Filter by month if selected
            const params = { year: selectedYear };
            if (selectedMonth) {
                params.month = selectedMonth;
            }
            const { data } = await axios.get(`${API}/salary`, { params });
            setRecords(data);
        } catch (err) {
            console.error('Error fetching records:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPrivateCredits = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/private-credits`);
            setPrivateCredits(data);
        } catch (err) {
            console.error('Error fetching private credits:', err);
            // If 403, user doesn't have access
            if (err.response?.status === 403) {
                setPrivateCredits([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record.id);
        setEditValues({ 
            rata: record.rata || 0, 
            paliwo: record.paliwo || 0,
            paliwo_items: record.paliwo_items || []
        });
    };

    const handleAddPaliwo = (recordId) => {
        if (editingRecord !== recordId) {
            // Start editing this record first
            const record = records.find(r => r.id === recordId);
            setEditingRecord(recordId);
            setEditValues({ 
                rata: record.rata || 0, 
                paliwo: record.paliwo || 0,
                paliwo_items: [...(record.paliwo_items || []), 0]
            });
        } else {
            setEditValues(prev => ({
                ...prev,
                paliwo_items: [...prev.paliwo_items, 0]
            }));
        }
        setShowAddMenu(null);
    };

    const handleRemovePaliwoItem = (index) => {
        setEditValues(prev => ({
            ...prev,
            paliwo_items: prev.paliwo_items.filter((_, i) => i !== index)
        }));
    };

    const handlePaliwoItemChange = (index, value) => {
        const numValue = value === '' ? 0 : (parseFloat(value) || 0);
        setEditValues(prev => ({
            ...prev,
            paliwo_items: prev.paliwo_items.map((v, i) => i === index ? numValue : v)
        }));
    };

    const handleSave = async (recordId) => {
        setSaving(true);
        try {
            await axios.put(`${API}/salary/${recordId}/employee-update`, editValues);
            setEditingRecord(null);
            fetchRecords();
        } catch (err) {
            console.error('Error saving:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditingRecord(null);
        setEditValues({ rata: 0, paliwo: 0, paliwo_items: [] });
    };

    const groupedByMonth = records.reduce((acc, record) => {
        const key = record.month;
        if (!acc[key]) acc[key] = [];
        acc[key].push(record);
        return acc;
    }, {});

    const isDniowkiMode = user?.salary_config?.mode === "dniowki";

    const NAV_ITEMS = [
        { key: 'home', label: 'Start', Icon: House },
        { key: 'salary', label: 'Wynagrodzenia', Icon: Calendar },
        ...(isDniowkiMode ? [{ key: 'dniowki', label: 'Moje dniówki', Icon: CalendarBlank }] : []),
        { key: 'paliwo', label: 'Paliwo', Icon: GasPump },
        { key: 'pozyczka', label: 'Pożyczki', Icon: Wallet },
        { key: 'private-credits', label: 'Moje kredyty', Icon: CurrencyCircleDollar },
        { key: 'pojazdy', label: 'Pojazdy', Icon: Car },
        { key: 'przebiegi', label: 'Przebiegi', Icon: Gauge },
        { key: 'urlopy', label: 'Urlopy', Icon: CalendarBlank },
        { key: 'kontakty', label: 'Kontakty', Icon: Phone },
        ...(hasRejon ? [{ key: 'rejony', label: 'Rejony', Icon: MapPin }] : []),
        { key: 'przypomnienia', label: 'Przypomnienia', Icon: BellRinging },
        { key: 'dyzury', label: 'Dyżury', Icon: CalendarCheck },
        { key: 'documents', label: 'Dokumenty', Icon: FileText },
        { key: 'chat', label: 'Czat', Icon: ChatCircle },
    ];

    const _activeItem = NAV_ITEMS.find(i => i.key === tab);
    const activeLabel = _activeItem ? _activeItem.label : '';
    const periodLabel = selectedMonth
        ? `${MONTHS[selectedMonth - 1]} ${selectedYear}`
        : `Rok ${selectedYear}`;

    return (
        <div className="min-h-screen bg-background flex">
            {/* Vertical Dark Sidebar */}
            <aside
                className={`${sidebarExpanded ? 'w-56' : 'w-14'} bg-[#0B1E3F] text-slate-300 flex flex-col py-3 gap-1 shrink-0 sticky top-0 h-screen z-30 overflow-y-auto transition-all duration-200`}
                data-testid="employee-sidebar"
            >
                <div className={`flex items-center ${sidebarExpanded ? 'px-3' : 'justify-center'} mb-3 shrink-0`}>
                    <div className="w-8 h-8 border border-white/30 flex items-center justify-center rounded-md">
                        <span className="font-heading font-bold text-sm text-white">A</span>
                    </div>
                    {sidebarExpanded && (
                        <span className="ml-3 font-heading font-bold text-white text-base tracking-tight">AODEX</span>
                    )}
                </div>
                {NAV_ITEMS.map(({ key, label, Icon }) => (
                    <a
                        key={key}
                        href={key === 'home' ? '?' : `?tab=${key}`}
                        onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                            e.preventDefault();
                            setTab(key);
                        }}
                        title={label}
                        aria-label={label}
                        data-testid={`tab-${key}`}
                        className={`${sidebarExpanded ? 'mx-2 px-3 justify-start gap-3' : 'mx-2 justify-center'} h-10 flex items-center shrink-0 rounded-md transition-colors relative no-underline ${
                            tab === key
                                ? 'bg-[#2563EB] text-white shadow-md'
                                : 'text-slate-400 hover:bg-white/10 hover:text-white'
                        }`}
                    >
                        <Icon className="w-5 h-5 shrink-0" weight={tab === key ? 'fill' : 'regular'} />
                        {sidebarExpanded && <span className="text-sm font-medium truncate">{label}</span>}
                        {key === 'chat' && chatUnread > 0 && (
                            <span
                                className={`${sidebarExpanded ? 'ml-auto' : 'absolute -top-1 -right-1'} min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center`}
                                data-testid="chat-badge"
                            >
                                {chatUnread > 99 ? '99+' : chatUnread}
                            </span>
                        )}
                    </a>
                ))}
                <div className="flex-1 min-h-[8px]" />
                <button
                    onClick={() => setShowChangePassword(true)}
                    title="Zmień hasło"
                    aria-label="Zmień hasło"
                    data-testid="change-password-btn"
                    className={`${sidebarExpanded ? 'mx-2 px-3 justify-start gap-3' : 'mx-2 justify-center'} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
                >
                    <Key className="w-5 h-5 shrink-0" />
                    {sidebarExpanded && <span className="text-sm font-medium">Zmień hasło</span>}
                </button>
                <button
                    onClick={logout}
                    title="Wyloguj"
                    aria-label="Wyloguj"
                    data-testid="logout-btn"
                    className={`${sidebarExpanded ? 'mx-2 px-3 justify-start gap-3' : 'mx-2 justify-center'} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
                >
                    <SignOut className="w-5 h-5 shrink-0" />
                    {sidebarExpanded && <span className="text-sm font-medium">Wyloguj</span>}
                </button>
                <div className="border-t border-white/10 mx-2 my-2" />
                <button
                    onClick={() => setSidebarExpanded((v) => !v)}
                    title={sidebarExpanded ? 'Zwiń menu' : 'Rozwiń menu'}
                    aria-label={sidebarExpanded ? 'Zwiń menu' : 'Rozwiń menu'}
                    data-testid="sidebar-toggle"
                    className={`${sidebarExpanded ? 'mx-2 px-3 justify-start gap-3' : 'mx-2 justify-center'} h-10 flex items-center shrink-0 rounded-md text-slate-400 hover:bg-white/10 hover:text-white`}
                >
                    {sidebarExpanded ? (
                        <>
                            <CaretDoubleLeft className="w-5 h-5 shrink-0" />
                            <span className="text-sm font-medium">Zwiń menu</span>
                        </>
                    ) : (
                        <CaretDoubleRight className="w-5 h-5 shrink-0" />
                    )}
                </button>
            </aside>

            {/* Main column */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Sticky Header */}
                <header
                    className="sticky top-0 bg-white border-b border-border px-4 md:px-8 py-3 md:py-4 flex items-center justify-between z-20"
                    data-testid="employee-header"
                >
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
                            {activeLabel}
                        </div>
                        <div className="flex items-baseline gap-3 mt-1">
                            <span
                                className="font-mono text-base md:text-2xl font-medium tracking-tight truncate"
                                data-testid="header-period-label"
                            >
                                {periodLabel}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <NotificationBell />
                        <PushBell />
                        <ThemeToggle />
                        <div className="hidden md:block text-right">
                            <h1
                                className="font-heading text-sm md:text-base font-bold tracking-tight leading-tight"
                                data-testid="dashboard-title"
                            >
                                WYN 2026
                            </h1>
                            <p className="text-xs text-muted-foreground leading-tight">
                                {user?.name}
                            </p>
                        </div>
                    </div>
                </header>

            {/* Main */}
            <main className="max-w-[1920px] mx-auto px-3 sm:px-6 py-4 sm:py-6 md:py-8 w-full">
                {/* Notification Banner */}
                {bannerNotification && (
                    <NotificationBanner
                        notification={bannerNotification}
                        onDismiss={() => setBannerNotification(null)}
                    />
                )}
                
                {tab === 'home' && (
                    <EmployeeHome
                        onNavigate={(t) => setTab(t)}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        user={user}
                    />
                )}
                {tab === 'chat' && <ChatTab user={user} />}

                {tab === 'salary' && (
                    <>
                        {/* Employee Yearly Stats Card */}
                        {records.length > 0 && (
                            <div className="mb-6">
                                <EmployeeYearlyStats 
                                    employeeName={user?.name || 'Pracownik'}
                                    records={records}
                                    selectedYear={selectedYear}
                                    selectedMonth={selectedMonth}
                                />
                            </div>
                        )}

                        {/* Filters */}
                        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                            <Calendar className="w-5 h-5 text-muted-foreground hidden sm:block" />
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className="input-field w-full sm:w-auto pr-10"
                                data-testid="year-select"
                            >
                                {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                            <select
                                value={selectedMonth || ''}
                                onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
                                className="input-field w-full sm:w-auto pr-10"
                                data-testid="month-select"
                            >
                                <option value="">Wszystkie miesiące</option>
                                {MONTHS.map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </select>
                        </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                ) : records.length === 0 ? (
                    <div className="text-center py-20 text-muted-foreground" data-testid="no-records">
                        <CurrencyCircleDollar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Brak danych za {selectedYear} rok</p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {Object.keys(groupedByMonth).sort((a, b) => a - b).map(month => {
                            const monthRecords = groupedByMonth[month];
                            const podsumowanie = calculatePodsumowanie(monthRecords);
                            
                            // Calculate month-to-month percentage change for DOCHOD
                            const currentMonthDochod = monthRecords.reduce((sum, r) => sum + (r.dochod || 0), 0);
                            
                            // Get previous month
                            const prevMonth = parseInt(month) === 1 ? 12 : parseInt(month) - 1;
                            const prevYear = parseInt(month) === 1 ? selectedYear - 1 : selectedYear;
                            
                            // Find previous month records from ALL YEAR records (not filtered)
                            const prevMonthRecords = allYearRecords.filter(r => r.month === prevMonth && r.year === prevYear);
                            const prevMonthDochod = prevMonthRecords.reduce((sum, r) => sum + (r.dochod || 0), 0);
                            
                            // Calculate percentage change - show even if previous month is 0
                            const hasChange = currentMonthDochod > 0 && prevMonthDochod > 0;
                            const isNewMonth = currentMonthDochod > 0 && prevMonthDochod === 0;
                            const percentChange = hasChange 
                                ? ((currentMonthDochod - prevMonthDochod) / prevMonthDochod) * 100
                                : 0;
                            
                            // Debug logging
                            console.log(`Month ${month}: current=${currentMonthDochod}, prev=${prevMonthDochod}, hasChange=${hasChange}, isNewMonth=${isNewMonth}, %=${percentChange}`);
                            
                            return (
                                <div key={month}>
                                    <h2 className="font-heading text-xl font-bold mb-4" data-testid={`month-${month}`}>
                                        {MONTHS[month - 1]} {selectedYear}
                                    </h2>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        {monthRecords.sort((a, b) => {
                                            // Null-safe sorting for period_start
                                            const aStart = a.period_start || '';
                                            const bStart = b.period_start || '';
                                            return aStart.localeCompare(bStart);
                                        }).map(record => {
                                            // 2026-08-31: salary is strictly read-only for employees.
                                            const isEditing = false;
                                            const displayPaliwoItems = record.paliwo_items || [];
                                            const totalPaliwo = getTotalPaliwo(record);
                                            
                                            // Calculate custom deductions total
                                            const customDeductionsTotal = (record.custom_deductions || []).reduce(
                                                (sum, deduction) => sum + (parseFloat(deduction.amount) || 0),
                                                0
                                            );
                                            
                                            const displayPrzelew = calculatePrzelew(record);
                                            
                                            return (
                                                <div key={record.id} className="bg-white border border-gray-100 rounded-sm shadow-lg hover:border border-border p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1" data-testid={`salary-card-${record.id}`}>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <span className="px-3 py-1.5 bg-muted text-foreground rounded-full text-sm font-bold border border-border shadow-sm">
                                                            {record.period_start === '15' ? '16' : (record.period_start || '??')}-{record.period_end || '??'}.{String(record.month).padStart(2, '0')}
                                                        </span>
                                                        
                                                        {/* Read-only for employees — additions happen in Paliwo/Pożyczka tabs */}
                                                    </div>

                                                    {/* Prominent Przelew display + Szczegóły toggle */}
                                                    {!isEditing && (
                                                        <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                                            <div className="text-xs uppercase tracking-wider text-emerald-600 font-bold mb-1">
                                                                Do wypłaty
                                                            </div>
                                                            <div className="text-3xl font-extrabold text-emerald-700 tabular-nums">
                                                                {displayPrzelew.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł
                                                            </div>
                                                            {payoutDateFromRecord(record) && (
                                                                <div
                                                                    className="mt-1 text-xs text-emerald-600 font-medium"
                                                                    data-testid={`payout-date-${record.id}`}
                                                                    title="Faktyczna wypłata (2 tygodnie po zakończeniu okresu)"
                                                                >
                                                                    Data wypłaty: {payoutDateFromRecord(record)}
                                                                </div>
                                                            )}
                                                            <button
                                                                onClick={() => {
                                                                    setExpandedRecords((prev) => {
                                                                        const n = new Set(prev);
                                                                        if (n.has(record.id)) n.delete(record.id);
                                                                        else n.add(record.id);
                                                                        return n;
                                                                    });
                                                                }}
                                                                className="mt-3 text-sm text-emerald-700 hover:text-emerald-900 font-semibold flex items-center gap-1"
                                                                data-testid={`toggle-details-${record.id}`}
                                                            >
                                                                {expandedRecords.has(record.id) ? (
                                                                    <><CaretUp className="w-4 h-4" /> Ukryj szczegóły</>
                                                                ) : (
                                                                    <><CaretDown className="w-4 h-4" /> Szczegóły</>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {(expandedRecords.has(record.id) || isEditing) && (
                                                    <>
                                                    <div className="space-y-3">
                                                        {/* Regular fields */}
                                                        {Object.entries(FIELD_LABELS).map(([key, label]) => {
                                                            // Skip paliwo and przelew - we'll handle them separately
                                                            if (key === 'paliwo' || key === 'przelew') return null;
                                                            
                                                            return (
                                                                <div key={key} className="flex items-center justify-between">
                                                                    <span className="field-label">
                                                                        {label}
                                                                        {key === 'rata' && (() => {
                                                                            const info = record.rata_info || record.rata_note;
                                                                            return info && typeof info === 'string' && info.trim() ? (
                                                                                <span className="ml-1 text-blue-500 text-xs">({info})</span>
                                                                            ) : null;
                                                                        })()}
                                                                    </span>
                                                                    {isEditing && key === 'rata' ? (
                                                                        <input
                                                                            type="number"
                                                                            value={editValues.rata}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                                                                                setEditValues(prev => ({ ...prev, rata: value }));
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value === '') {
                                                                                    setEditValues(prev => ({ ...prev, rata: 0 }));
                                                                                }
                                                                            }}
                                                                            className="input-field w-28 text-right"
                                                                            data-testid={`edit-rata-input`}
                                                                        />
                                                                    ) : (
                                                                        <span className={`field-value ${key === 'rata' ? 'accent-blue' : ''}`}>
                                                                            {typeof record[key] === 'number' 
                                                                                ? record[key].toLocaleString('pl-PL', { minimumFractionDigits: 2 })
                                                                                : record[key] || '0,00'} zł
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                        
                                                        {/* Paliwo — legacy display for older records. New records show paliwo inside Potrącenia. */}
                                                        {(record.paliwo > 0 || displayPaliwoItems.length > 0) && (
                                                        <div className="border-t border-border pt-3 mt-3">
                                                            {record.paliwo > 0 && (
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="field-label">Paliwo (base)</span>
                                                                    <span className="field-value accent-blue">
                                                                        {(record.paliwo || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Additional paliwo items */}
                                                            {displayPaliwoItems.map((item, idx) => (
                                                                <div key={idx} className="flex items-center justify-between mb-2 pl-4">
                                                                    <span className="field-label text-green-600">+ Paliwo #{idx + 1}</span>
                                                                    <span className="field-value text-green-600">
                                                                        {item.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            
                                                            {/* Total paliwo */}
                                                            {displayPaliwoItems.length > 0 && (
                                                                <div className="flex items-center justify-between mb-2 pl-4 font-semibold">
                                                                    <span className="field-label">= Paliwo razem</span>
                                                                    <span className="field-value">
                                                                        {totalPaliwo.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        )}
                                                        
                                                        {/* Custom deductions display — unified "Potrącenia" list (collapsed sum + expand) */}
                                                        {(((record.rata_details && record.rata_details.length > 0) || (record.custom_deductions && record.custom_deductions.length > 0) || (record.rata && record.rata > 0))) && (() => {
                                                            const filteredCustom = (record.custom_deductions || []).filter((d) => {
                                                                const rawName = (d?.name || '').trim();
                                                                const src = (d?.source || '').toLowerCase();
                                                                return !(src === 'rata_kredyt' || /^rata\b/i.test(rawName));
                                                            });
                                                            const rataDetailsList = record.rata_details || [];
                                                            const hasLegacyRata = record.rata > 0 && !(record.custom_deductions || []).some(d => d?.source === "rata_kredyt");
                                                            const totalRata = rataDetailsList.length > 0
                                                                ? rataDetailsList.reduce((s, d) => s + (Number(d.amount) || 0), 0)
                                                                : (hasLegacyRata ? Number(record.rata) || 0 : 0);
                                                            const totalCustom = filteredCustom.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
                                                            const totalDeductions = totalRata + totalCustom;
                                                            const rowsCount = rataDetailsList.length + filteredCustom.length + (rataDetailsList.length === 0 && hasLegacyRata ? 1 : 0);
                                                            const isExpanded = !!expandedPotracenia[record.id];
                                                            return (
                                                                <div className="border-t border-border pt-3">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setExpandedPotracenia((prev) => ({ ...prev, [record.id]: !prev[record.id] }))}
                                                                        className="w-full flex items-center justify-between gap-2 hover:bg-orange-50 rounded-md px-2 py-1.5"
                                                                        data-testid={`emp-potracenia-toggle-${record.id}`}
                                                                    >
                                                                        <span className="field-label font-semibold text-orange-600">Potrącenia</span>
                                                                        <span className="flex items-center gap-2">
                                                                            <span className="text-[10px] uppercase tracking-wider text-orange-500">
                                                                                {isExpanded ? 'Ukryj' : `Szczegóły (${rowsCount})`}
                                                                            </span>
                                                                            <span className="field-value font-bold text-orange-600 tabular-nums">
                                                                                -{totalDeductions.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                            </span>
                                                                        </span>
                                                                    </button>
                                                                    {isExpanded && (
                                                                        <div className="mt-2 space-y-1">
                                                                            {(() => {
                                                                                const rataDetails = rataDetailsList;
                                                                                if (rataDetails.length > 0) {
                                                                                    return rataDetails.map((d, i) => {
                                                                                        const dueSuffix = d.due_date ? ` — ${d.due_date}` : '';
                                                                                        const numSuffix = d.payment_number != null && d.total_payments
                                                                                            ? ` (${d.payment_number}/${d.total_payments})`
                                                                                            : '';
                                                                                        return (
                                                                                            <div key={`rd-${i}`} className="flex items-center justify-between mb-1 pl-2 gap-2">
                                                                                                <div className="flex items-center gap-2 min-w-0">
                                                                                                    <span className="inline-block text-[9px] font-mono px-1 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wide shrink-0">Rata</span>
                                                                                                    <span className="field-label text-sm truncate" title={`${d.credit_name}${numSuffix}${dueSuffix}`}>
                                                                                                        {d.credit_name}{numSuffix}{dueSuffix}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <span className="field-value text-orange-600 whitespace-nowrap">
                                                                                                    -{Number(d.amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                                                </span>
                                                                                            </div>
                                                                                        );
                                                                                    });
                                                                                }
                                                                                if (hasLegacyRata) {
                                                                                    return (
                                                                                        <div className="flex items-center justify-between mb-1 pl-2 gap-2">
                                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                                <span className="inline-block text-[9px] font-mono px-1 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wide shrink-0">Rata</span>
                                                                                                <span className="field-label text-sm truncate">{record.rata_info || record.rata_note || "Rata"}</span>
                                                                                            </div>
                                                                                            <span className="field-value text-orange-600 whitespace-nowrap">
                                                                                                -{parseFloat(record.rata).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                            {filteredCustom.map((deduction, idx) => (
                                                                                <div key={idx} className="flex items-center justify-between mb-1 pl-2 gap-2">
                                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                                        {deduction?.source && (
                                                                                            <span className="inline-block text-[9px] font-mono px-1 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wide shrink-0">
                                                                                                {deduction.source === "paliwo" ? "Paliwo" :
                                                                                                 deduction.source === "rata_kredyt" ? "Rata" :
                                                                                                 deduction.source === "pozyczka" ? "Pożyczka" :
                                                                                                 deduction.source === "usluga" ? "Usługa" : "Auto"}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className="field-label text-sm truncate" title={deduction.name}>{deduction.name}</span>
                                                                                    </div>
                                                                                    <span className="field-value text-orange-600 whitespace-nowrap">
                                                                                        -{parseFloat(deduction.amount).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        
                                                        {/* Przelew - recalculated */}
                                                        <div className="flex items-center justify-between border-t border-border pt-3">
                                                            <span className="field-label font-semibold">Przelew</span>
                                                            <span className="field-value font-semibold text-lg">
                                                                {displayPrzelew.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 pt-4 border-t border-border flex justify-end gap-2">
                                                        <span className="text-xs text-slate-400 italic">Widok podglądowy — zmiany może wprowadzić tylko administrator</span>
                                                    </div>
                                                    </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Podsumowanie - Premium Design */}
                                    {podsumowanie && (
                                        <div className="mt-5 p-5 bg-emerald-50 border-2 border-emerald-300 rounded-md shadow-sm transition-all duration-300" data-testid={`podsumowanie-${month}`}>
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-emerald-500 p-2 rounded-xl shadow-md">
                                                        <span className="text-white text-xl font-bold">✓</span>
                                                    </div>
                                                    <h3 className="text-emerald-700 font-extrabold uppercase text-lg tracking-wide">
                                                        Podsumowanie {MONTHS[month - 1]}
                                                    </h3>
                                                </div>
                                                {/* Month-to-month percentage change */}
                                                {(hasChange || isNewMonth) && (
                                                    <div className={`flex items-center gap-2 font-bold px-4 py-2 rounded-full shadow-md ${
                                                        isNewMonth ? 'bg-blue-100 text-blue-700' : (percentChange >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')
                                                    }`}>
                                                        {isNewMonth ? (
                                                            <span className="text-lg">NOWY</span>
                                                        ) : (
                                                            <>
                                                                {percentChange >= 0 ? (
                                                                    <TrendUp className="w-6 h-6" weight="bold" />
                                                                ) : (
                                                                    <TrendDown className="w-6 h-6" weight="bold" />
                                                                )}
                                                                <span className="text-lg">
                                                                    {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {Object.entries(FIELD_LABELS).map(([key, label]) => (
                                                    <div key={key} className="flex flex-col bg-white p-3 rounded-xl hover:bg-emerald-50/50 transition-colors">
                                                        <span className="field-label text-emerald-600 font-semibold mb-2 text-sm uppercase tracking-wide">{label}</span>
                                                        <span className="text-2xl font-bold text-emerald-700 tabular-nums">
                                                            {podsumowanie[key]?.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                    </>
                )}

                {tab === 'private-credits' && (
                    <div>
                        <h2 className="font-heading text-xl font-bold mb-6">Moje kredyty prywatne</h2>
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : privateCredits.length === 0 ? (
                            <div className="text-center py-20 text-muted-foreground">
                                Brak przypisanych kredytów.
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {privateCredits.map(credit => (
                                    <div key={credit.id} className="bg-white border border-border rounded-sm p-4 sm:p-6">
                                        <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4">{credit.name}</h3>
                                        
                                        {/* Payment schedule table */}
                                        <div className="overflow-x-auto -mx-1">
                                            <table className="w-full text-sm min-w-[520px]">
                                                <thead className="bg-muted">
                                                    <tr>
                                                        <th className="text-left py-2 px-2 sm:px-3 field-label whitespace-nowrap">Nr</th>
                                                        <th className="text-left py-2 px-2 sm:px-3 field-label whitespace-nowrap">Data</th>
                                                        <th className="text-right py-2 px-2 sm:px-3 field-label whitespace-nowrap">Kwota</th>
                                                        <th className="text-right py-2 px-2 sm:px-3 field-label whitespace-nowrap">Pozostało</th>
                                                        <th className="text-center py-2 px-2 sm:px-3 field-label whitespace-nowrap">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {credit.payments.map((payment, idx) => (
                                                        <tr key={idx} className={payment.paid ? 'bg-green-50' : ''}>
                                                            <td className="py-2 px-2 sm:px-3">{payment.payment_number}</td>
                                                            <td className="py-2 px-2 sm:px-3 whitespace-nowrap">{payment.date}</td>
                                                            <td className="py-2 px-2 sm:px-3 text-right tabular-nums whitespace-nowrap">{payment.amount.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                                                            <td className="py-2 px-2 sm:px-3 text-right tabular-nums whitespace-nowrap">{payment.remaining.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                                                            <td className="py-2 px-2 sm:px-3 text-center whitespace-nowrap">
                                                                {payment.paid ? (
                                                                    <span className="text-green-600 font-medium">✓ Opłacono</span>
                                                                ) : (
                                                                    <span className="text-orange-600">Oczekuje</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Summary */}
                                        <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                            <div className="text-sm">
                                                <span className="field-label">Opłaconych rat:</span>
                                                <span className="ml-2 font-semibold">
                                                    {credit.payments.filter(p => p.paid).length} / {credit.payments.length}
                                                </span>
                                            </div>
                                            <div className="text-sm">
                                                <span className="field-label">Pozostało do spłaty:</span>
                                                <span className="ml-2 font-semibold">
                                                    {credit.payments
                                                        .filter(p => !p.paid)
                                                        .reduce((sum, p) => sum + p.amount, 0)
                                                        .toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                
                {tab === 'pojazdy' && <EmployeePojazdyTab />}
                {tab === 'przebiegi' && <PrzebiegiTab pojazdy={[]} />}
                {tab === 'paliwo' && <EmployeePaliwoTab />}
                {tab === 'pozyczka' && <EmployeePozyczkaTab user={user} />}
                
                {tab === 'urlopy' && <UrlowyTab />}
                {tab === 'kontakty' && <KontaktyTab />}
                {tab === 'rejony' && <RejonyTab />}
                {tab === 'przypomnienia' && <PrzypomnieniaTab />}
                {tab === 'dyzury' && <DyzuryTab />}
                {tab === 'dniowki' && <DniowkiTab />}
                {tab === 'documents' && (
                    <EmployeeDocumentsTab
                        onView={(docId) => {
                            const token = localStorage.getItem('access_token');
                            window.open(`${API}/documents/${docId}?token=${token}`, '_blank');
                        }}
                    />
                )}
            </main>
            </div>{/* /main column */}

            {/* Change Password Modal */}
            <ChangePasswordModal
                isOpen={showChangePassword}
                onClose={() => setShowChangePassword(false)}
                isRequired={user?.password_change_required}
            />
        </div>
    );
}
